(() => {
  const ROOT_SELECTOR = '[data-infinite-options-collapse]';
  const CONTROL_SELECTOR = '[data-infinite-options-control]';
  const OPTION_SELECTOR = 'input[type="checkbox"], input[type="radio"], select';
  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [data-infinite-options-control]';
  const HIDDEN_CLASS = 'infinite-options-collapse__hidden';
  const ROW_CLASS = 'infinite-options-collapse__row';

  const unique = (items) => [...new Set(items.filter(Boolean))];

  const optionRowForInput = (input, root) => {
    const labeledRow = input.closest('label');
    if (labeledRow && root.contains(labeledRow) && !labeledRow.matches(ROOT_SELECTOR)) return labeledRow;

    let row = input;
    let parent = input.parentElement;
    while (parent && parent !== root) {
      const inputCount = parent.querySelectorAll(OPTION_SELECTOR).length;
      if (inputCount > 1) break;
      row = parent;
      parent = parent.parentElement;
    }
    return row === input ? input.parentElement : row;
  };

  const getOptionRows = (root) =>
    unique([...root.querySelectorAll(OPTION_SELECTOR)].filter((input) => !input.closest(CONTROL_SELECTOR)).map((input) => optionRowForInput(input, root)));

  const getToggle = (root) => root.querySelector(CONTROL_SELECTOR);

  const toggleRoot = (root) => {
    root.dataset.infiniteOptionsExpanded = root.dataset.infiniteOptionsExpanded === 'true' ? 'false' : 'true';
    refresh(root);
  };

  const ensureToggle = (root) => {
    let button = getToggle(root);
    if (button) return button;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'infinite-options-collapse__toggle';
    button.setAttribute('data-infinite-options-control', '');
    button.setAttribute('aria-label', 'Show options');

    button.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      toggleRoot(root);
    });

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!window.PointerEvent) toggleRoot(root);
    });

    button.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      toggleRoot(root);
    });

    root.append(button);
    return button;
  };

  const removeToggle = (root) => {
    getToggle(root)?.remove();
  };

  function refresh(root) {
    const rows = getOptionRows(root);
    const expanded = root.dataset.infiniteOptionsExpanded === 'true';

    root.querySelectorAll(`.${HIDDEN_CLASS}`).forEach((item) => {
      if (!rows.includes(item)) item.classList.remove(HIDDEN_CLASS);
    });

    root.querySelectorAll(`.${ROW_CLASS}`).forEach((item) => {
      if (!rows.includes(item)) item.classList.remove(ROW_CLASS);
    });

    if (!rows.length) {
      root.classList.remove('is-collapsible', 'is-collapsed', 'is-expanded');
      removeToggle(root);
      return;
    }

    const button = ensureToggle(root);
    root.classList.add('is-collapsible');
    root.classList.toggle('is-expanded', expanded);
    root.classList.toggle('is-collapsed', !expanded);
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.setAttribute('aria-label', expanded ? 'Hide options' : 'Show options');

    rows.forEach((row) => {
      row.classList.add(ROW_CLASS);
      row.classList.toggle(HIDDEN_CLASS, !expanded);
    });
  }

  const schedule = (root) => {
    window.clearTimeout(root.infiniteOptionsCollapseTimer);
    root.infiniteOptionsCollapseTimer = window.setTimeout(() => refresh(root), 80);
  };

  const initRoot = (root) => {
    if (root.infiniteOptionsCollapseReady) {
      schedule(root);
      return;
    }

    root.infiniteOptionsCollapseReady = true;
    root.dataset.infiniteOptionsExpanded = root.dataset.infiniteOptionsDefaultExpanded === 'true' ? 'true' : 'false';

    root.addEventListener('click', (event) => {
      if (!root.classList.contains('is-collapsed')) return;
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;
      event.preventDefault();
      toggleRoot(root);
    });

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !mutation.target.closest?.(CONTROL_SELECTOR))) schedule(root);
    });

    observer.observe(root, { childList: true, subtree: true });
    schedule(root);
  };

  const init = (container = document) => {
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  window.KGYRefreshInfiniteOptionsCollapse = (root) => {
    if (!root) return;
    initRoot(root);
    refresh(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
